import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ArticleYearlyBreakdownView } from '../_generated/model/articleYearlyBreakdownView';
import { ArticleSummaryView } from '../_generated/model/models';
import { HeaderComponent } from '../layout/header/header.component';
import { HeaderStyle } from '../layout/header/header.desktop.responsive';
import { getTimeAgoOrExactDate } from '../shared/utils';
import { UseUIBasicIconDirective } from '../ui-components/atoms/icons/ui-basic.directive';
import { DropdownMenuComponent } from '../ui-components/organisms/dropdown-menu.component';
import { ArticlesActions, articlesFeature } from './';

// Type for saved article list state
interface ArticleListReturnState {
  returnYear: number;
  returnArticleId: number;
  returnStartIndex: number;
  returnScroll: number;
}

@Component({
  selector: 'dmcom-article-list',
  imports: [TranslateModule, UseUIBasicIconDirective, DropdownMenuComponent, CdkMenuTrigger, CommonModule, FormsModule, HeaderComponent],
  animations: [
    trigger('scrollbarVisibility', [
      state(
        'visible',
        style({
          opacity: 1,
        }),
      ),
      state(
        'hidden',
        style({
          opacity: 0,
        }),
      ),
      transition('hidden => visible', [animate('0.3s ease-out')]),
      transition('visible => hidden', [animate('0.3s ease-in')]),
    ]),
  ],
  template: `
    <dmcom-header [headerStyle]="HeaderStyle.transparent"></dmcom-header>
    <div
      #scrollableContainer
      class="h-full w-full flex flex-col relative scrollable-component border-4 border-white rounded-xl"
      [class.scrollbar-visible]="scrollbarVisible"
    >
      <div
        class="h-[280px] w-full rounded-lg flex flex-col justify-end flex-shrink-0"
        style="background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.88) 80%), linear-gradient(85deg, var(--Primary-200, #B8A7E3) 0%, var(--Primary-100, #DCD3F1) 100%);"
      >
        <div class="flex flex-col gap-20 mt-[132px] mb-[40px] w-[680px] mx-auto">
          <p class="typo-h6 text-left font-normal">
            <span
              class="font-semibold"
              style="background: linear-gradient(89deg, var(--Primary-800, #1F0E4A) 0.86%, var(--Primary-500, #4E22BA) 29.76%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;"
              >Compliance <span class="italic">Edge</span><span class="trademark">TM</span></span
            >
            <span class="text-primary-700"> is the most up-to-date compliance information available in the industry.</span>
          </p>
          <p class="typo-p3 text-left mt-4 flex items-center justify-start gap-8 text-primary-700">
            <span>Curated by</span>
            <img src="/assets/logos/logo-docmagic-long.svg" class=" h-[24px]" />
          </p>
        </div>
      </div>
      <div class="flex-1 flex items-start justify-center pt-8 min-h-0">
        <div
          class="w-[680px] grid relative"
          style="grid-template-rows: auto auto; grid-template-columns: 540px 1fr; grid-column-gap: 20px;"
        >
          <!-- Top Left: Date Selector -->
          <div class="sticky-year-selector flex items-baseline pt-20 pb-20 pr-16 pl-16 border-b border-neutral-600">
            <h1 class="text-[18px] text-grayscale-gray-dark">Compliance Year:</h1>
            <div class="w-4"></div>
            <button
              [cdkMenuTriggerFor]="yearMenu"
              class="flex items-center gap-2 px-3 py-2 bg-transparent hover:bg-gray-100 focus:outline-none rounded cursor-pointer"
            >
              <span class="text-[18px] font-normal">{{ selectedYear() }}</span>
              <svg dmcomUseUIBasicIcon="chevron-down" class="w-16 h-16 text-gray-600"></svg>
            </button>
            <ng-template #yearMenu>
              <dmcom-dropdown-menu [options]="yearOptions()"></dmcom-dropdown-menu>
            </ng-template>
          </div>

          <!-- Top Right: Empty Space -->
          <div class="sticky-empty-space border-b border-neutral-600"></div>

          <!-- Bottom Left: Articles List -->
          <div class="flex flex-col">
            <!-- Loading state (only for initial load) -->
            @if (loading() && !isLoadingMore()) {
              <div class="mt-4 text-center py-8">
                <p class="text-grayscale-gray-dark">Loading articles...</p>
              </div>
            }

            <!-- Month loading state - subtle indicator -->
            @if (isLoadingMonth()) {
              <div class="sticky top-0 bg-white/90 backdrop-blur-sm border-b border-primary-200 px-4 py-2 z-50">
                <div class="flex items-center justify-center gap-2">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                  <p class="text-primary-600 text-xs font-medium">Loading articles for selected month...</p>
                </div>
              </div>
            }

            <!-- Error state -->
            @if (error(); as error) {
              <div class="mt-4 text-center py-8">
                <p class="text-error-500">Error loading articles: {{ error }}</p>
              </div>
            }

            <!-- Articles list -->
            @if ((!loading() || isLoadingMore()) && !error()) {
              <div class="flex flex-col relative">
                @for (article of getSortedArticles(); track article.nodeId; let first = $first) {
                  <button
                    [attr.data-article-id]="article.nodeId"
                    [class]="
                      'w-full text-left p-20 flex flex-col justify-center bg-transparent cursor-pointer hover-bg-custom' +
                      (first ? '' : ' border-t border-neutral-600')
                    "
                    (click)="viewArticle(article.nodeId)"
                  >
                    <p class="text-[12px] flex items-center gap-2 text-primary-500 leading-none">
                      <svg
                        [dmcomUseUIBasicIcon]="article.category === 'form_update' ? 'form' : 'legal'"
                        class="w-[22px] h-[22px] flex-shrink-0"
                      ></svg>
                      <span class="translate-y-[1.5px]">{{ getCategoryDisplayName(article.category) }}</span>
                      <span class="text-grayscale-gray-dark translate-y-[1.5px]">•</span>
                      <span class="text-grayscale-gray-dark translate-y-[1.5px]">{{ getTimeAgo(article.createdAt) }}</span>
                    </p>
                    <p class="text-[18px] font-semibold mt-8 text-headers">
                      {{ article.title }}
                    </p>
                  </button>
                }

                <!-- Loading more articles indicator - appears after the last article -->
                @if (isLoadingMore()) {
                  <div class="flex flex-col items-center justify-center py-6 bg-blue-50 border-2 border-blue-200 rounded-lg mx-4 my-4">
                    <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                    <p class="text-blue-700 text-sm font-medium">Loading more articles...</p>
                  </div>
                }

                <!-- Show remaining articles count -->
                @if (!isLoadingMore() && articles() && articles()!.length > visibleArticlesCount()) {
                  <div class="text-center py-4 text-sm text-gray-500">
                    Showing {{ visibleArticlesCount() }} of {{ articles()!.length }} articles
                  </div>
                }

                <!-- No articles message -->
                @if (!articles() || articles()!.length === 0) {
                  <div class="text-center py-8">
                    <p class="text-grayscale-gray-dark">No articles found for {{ selectedYear() }}</p>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Bottom Right: Months -->
          <div class="sticky-months flex flex-col relative z-10 self-start">
            <div class="pt-20 pb-20 pl-16 pr-16">
              <div class="flex flex-col gap-3">
                @for (monthData of monthsWithArticles(); track monthData.month) {
                  <div
                    class="month-item text-[14px] font-normal cursor-pointer"
                    [class.text-primary-500]="isMonthSelected(monthData.month)"
                    [class.text-neutral-200]="!isMonthSelected(monthData.month)"
                    (click)="selectMonth(monthData.month)"
                  >
                    {{ monthData.monthName }}
                    <sup class="text-[10px]">{{ monthData.count }}</sup>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom fade overlay for entire component -->
      <div
        class="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
        style="background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.80) 50%, #FFF 100%);"
      ></div>
    </div>
  `,
  styles: [
    `
      .hover-bg-custom {
        transition: background-color 0.2s;
      }
      .hover-bg-custom:hover {
        background-color: #f8f9fa;
      }

      .month-item {
        width: 120px;
        min-width: 120px;
        max-width: 120px;
      }

      .sticky-year-selector {
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 100;
      }

      .sticky-empty-space {
        position: sticky;
        top: 0;
        background-color: white;
        z-index: 10;
      }

      .sticky-months {
        position: sticky;
        top: 80px;
        background-color: white;
        z-index: 10;
      }

      /* Ensure dropdown menu appears above all other elements */
      ::ng-deep .cdk-overlay-container {
        z-index: 1000 !important;
      }

      ::ng-deep .cdk-overlay-pane {
        z-index: 1000 !important;
      }

      /* Most specific approach - target CDK overlay */
      ::ng-deep .cdk-overlay-pane [cdkMenu] button[cdkMenuItem]:hover {
        background-color: var(--color-gray-100, #f3f4f6) !important;
      }
    `,
  ],
})
export class ArticleListComponent implements OnInit, AfterViewInit {
  // Inject dependencies
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Selected month state
  public selectedMonth = signal<number | null>(null);

  // Track when user last manually selected a month
  private lastUserSelectionTime = 0;

  // Flag to track if we're coming from a page refresh (vs navigation)
  private isPageRefresh = true;

  /**
   * Public method to check if the page was refreshed
   * @returns true if page was refreshed, false if user navigated
   */
  public wasPageRefreshed(): boolean {
    return this.isPageRefresh;
  }

  /**
   * Public method to check if user navigated to this page
   * @returns true if user navigated, false if page was refreshed
   */
  public didUserNavigate(): boolean {
    return !this.isPageRefresh;
  }

  // Scrollbar visibility state
  public scrollbarVisible = false;

  // Utility functions for template
  public getTimeAgo = getTimeAgoOrExactDate;
  public readonly HeaderStyle = HeaderStyle;

  // Year options for dropdown
  public readonly yearOptions = computed(() => {
    const yearlyBreakdown = this.articlesYearlyBreakdown();
    return yearlyBreakdown.map((year) => ({
      label: year.year.toString(),
      action: () => this.onYearChange(year.year.toString()),
    }));
  });

  // ViewChild reference to scrollable container
  @ViewChild('scrollableContainer', { static: false })
  scrollableContainer!: ElementRef<HTMLElement>;

  // Signals from store
  public readonly articles = this.store.selectSignal(articlesFeature.selectArticles);
  public readonly selectedYear = this.store.selectSignal(articlesFeature.selectSelectedYear);
  public readonly loading = this.store.selectSignal(articlesFeature.selectIsLoading);
  public readonly error = this.store.selectSignal(articlesFeature.selectError);

  public readonly articlesYearlyBreakdown = this.store.selectSignal(articlesFeature.selectArticlesYearlyBreakdown);
  public readonly hasMorePages = this.store.selectSignal(articlesFeature.selectHasMorePages);

  // Pagination signals
  private readonly currentPage = signal(1);
  public readonly isLoadingMore = signal(false);

  // Track pagination loading state
  private isPaginationLoading = false;

  // Track month loading state
  public readonly isLoadingMonth = signal(false);

  // Store articles during month loading to prevent scroll jump
  private articlesDuringMonthLoad: ArticleSummaryView[] = [];

  // Track visible articles count for pagination
  public readonly visibleArticlesCount = signal(200);
  private readonly articlesPerLoad = 50;

  // Track the start index of visible articles for upward loading
  public readonly visibleStartIndex = signal(0);

  // Flag to disable auto-loading during month navigation animation
  private isNavigatingToMonth = false;

  // Constants for DOM selectors
  private readonly ARTICLE_SELECTOR = '[data-article-id]';

  private toNumber(value: number | string): number {
    return typeof value === 'number' ? value : parseInt(value);
  }

  /**
   * Parse saved state from localStorage with type safety
   */
  private parseSavedState(): ArticleListReturnState | null {
    const savedState = localStorage.getItem('articleListReturnState');
    if (!savedState) {
      return null;
    }

    try {
      const parsedState = JSON.parse(savedState) as ArticleListReturnState;
      parsedState.returnYear = this.toNumber(parsedState.returnYear);
      parsedState.returnArticleId = this.toNumber(parsedState.returnArticleId);
      parsedState.returnStartIndex = this.toNumber(parsedState.returnStartIndex);
      parsedState.returnScroll = this.toNumber(parsedState.returnScroll);

      return parsedState;
    } catch (e) {
      console.warn('Failed to parse saved state:', e);
      return null;
    }
  }

  ngOnInit() {
    this.store.dispatch(ArticlesActions.loadArticlesYearlyBreakdown());

    // Set up page refresh detection
    this.setupPageRefreshDetection();
  }

  /**
   * Get current scroll position
   */
  private getCurrentScrollPosition(): number {
    if (!this.scrollableContainer) {
      return 0;
    }
    return this.scrollableContainer.nativeElement.scrollTop;
  }

  /**
   * Set scroll position
   */
  private setScrollPosition(scrollTop: number): void {
    if (!this.scrollableContainer) {
      return;
    }
    this.scrollableContainer.nativeElement.scrollTop = scrollTop;
  }

  ngAfterViewInit() {
    // Use setTimeout to ensure the view is fully rendered
    setTimeout(() => {
      this.setupScrollbarVisibility();
      this.setupScrollListener();
    }, 100);
  }

  constructor() {
    effect(() => {
      const articlesYearlyBreakdown = this.articlesYearlyBreakdown();
      this.restoreSelectedYear(articlesYearlyBreakdown);
    });

    // Reset pagination when year changes
    effect(() => {
      const selectedYear = this.selectedYear();
      if (selectedYear) {
        this.currentPage.set(1);
        this.isLoadingMore.set(false);
        this.visibleArticlesCount.set(200); // Reset to initial 200 articles
        this.visibleStartIndex.set(0); // Reset start index
      }
    });

    // Restore scroll position only when articles are first loaded (not during pagination)
    effect(() => {
      const articles = this.articles();
      const loading = this.loading();
      const isLoadingMore = this.isLoadingMore();
      const currentPage = this.currentPage();

      // Only restore scroll position for initial load, not during pagination
      if (articles && articles.length > 0 && !loading && !isLoadingMore && currentPage === 1) {
        // Use a minimal delay to ensure the view is fully rendered
        setTimeout(() => {
          this.restoreArticlePosition();
        }, 50);
      }
    });

    // Listen to store actions to update pagination state
    effect(() => {
      const loading = this.loading();
      const isLoadingMore = this.isLoadingMore();
      const currentPage = this.currentPage();

      if (!loading && isLoadingMore) {
        // Loading more articles completed, update current page
        const newPage = currentPage + 1;
        this.currentPage.set(newPage);
        this.isLoadingMore.set(false);
        this.isPaginationLoading = false;

        // No need to restore scroll position - the articles are appended, not replaced
        // The scroll position should remain stable naturally
      }
    });
  }

  getCategoryDisplayName(category: string): string {
    return category === 'legal_regulatory_update' ? 'Legal & Regulatory Update' : 'Form Update';
  }

  // Check if month should be highlighted
  isMonthSelected(month: number): boolean {
    const selectedMonthValue = this.selectedMonth();
    const monthNumber = Number(month);
    return selectedMonthValue === monthNumber;
  }

  onYearChange(yearString: string) {
    const year = Number(yearString);
    this.store.dispatch(ArticlesActions.selectYear({ year }));
    // Reset user selection when changing year to allow auto-selection again
    this.lastUserSelectionTime = 0;
    this.selectedMonth.set(null);

    // Auto-select the dominant month after year change
    setTimeout(() => {
      this.updateSelectedMonthBasedOnVisibleArticles();
    }, 500); // Wait for articles to load and render
  }

  readonly monthsWithArticles = computed(() => {
    const yearlyBreakdown = this.articlesYearlyBreakdown();
    const selectedYear = this.selectedYear();

    const yearData = yearlyBreakdown.find((year) => Number(year.year) === Number(selectedYear));
    if (!yearData?.months) {
      return [];
    }

    // Sort months in descending order (December to January)
    return [...yearData.months].sort((a, b) => b.month - a.month);
  });

  selectMonth(month: number) {
    this.selectedMonth.set(Number(month));
    this.lastUserSelectionTime = Date.now();

    const monthNumber = Number(month);

    // Save current scroll position before making any changes
    const currentScrollPosition = this.getCurrentScrollPosition();

    // Register ScrollToPlugin
    gsap.registerPlugin(ScrollToPlugin);

    // Check if this is the first month in the list
    const monthsWithArticles = this.monthsWithArticles();
    const isFirstMonth = monthsWithArticles.length > 0 && Number(monthsWithArticles[0].month) === monthNumber;

    // If it's the first month, scroll to the very top to show the header
    if (isFirstMonth && this.isScrollableContainerAvailable()) {
      setTimeout(() => {
        const container = this.scrollableContainer.nativeElement;
        gsap.to(container, {
          duration: 1,
          scrollTop: 0,
          ease: 'power2.out',
        });
      }, 100);
      return; // Exit early since we've handled the first month case
    }

    // Store current articles to prevent scroll jump during loading
    this.articlesDuringMonthLoad = [...this.getAllSortedArticles()];

    // Set loading state for month navigation
    this.isLoadingMonth.set(true);
    this.isNavigatingToMonth = true; // Disable auto-loading during navigation

    // Month articles are already loaded since we fetch all articles for the year
    // Clear loading state
    this.isLoadingMonth.set(false);
    this.articlesDuringMonthLoad = []; // Clear stored articles

    // Find the first article of the selected month
    const articles = this.getAllSortedArticles();
    const firstArticleOfMonth = articles.find((article) => {
      const articleMonth = new Date(article.createdAt).getMonth() + 1; // getMonth() returns 0-11
      return articleMonth === monthNumber;
    });

    if (firstArticleOfMonth && this.isScrollableContainerAvailable()) {
      // Find the index of the first article of the selected month
      const firstArticleIndex = articles.findIndex((article) => article.nodeId === firstArticleOfMonth.nodeId);

      if (firstArticleIndex !== -1) {
        // Calculate the start index to show 100 articles before the selected month
        const targetStartIndex = Math.max(0, firstArticleIndex - 100);

        // Update the visible start index to show articles starting 100 before the selected month
        this.visibleStartIndex.set(targetStartIndex);

        // Use setTimeout to ensure DOM is updated
        setTimeout(() => {
          // Restore the user's previous scroll position before animation
          if (this.scrollableContainer?.nativeElement) {
            this.scrollableContainer.nativeElement.scrollTop = currentScrollPosition;
          }

          // Then animate to the month article
          setTimeout(() => {
            const articleElement = document.querySelector(`[data-article-id="${firstArticleOfMonth.nodeId}"]`);
            if (articleElement) {
              const container = this.scrollableContainer.nativeElement;
              const containerRect = container.getBoundingClientRect();
              const elementRect = articleElement.getBoundingClientRect();

              const currentScrollTop = container.scrollTop;
              const elementTopRelativeToContainer = elementRect.top - containerRect.top;
              const targetScrollTop = currentScrollTop + elementTopRelativeToContainer - 60;

              gsap.to(container, {
                duration: 1,
                scrollTop: targetScrollTop,
                ease: 'power2.out',
                onComplete: () => {
                  // Re-enable auto-loading after animation completes
                  this.isNavigatingToMonth = false;
                },
              });
            }
          }, 50); // Small delay to ensure scroll position is restored
        }, 100);
      }
    }
  }

  /**
   * Get all articles sorted by date (newest first) - complete list
   */
  getAllSortedArticles(): ArticleSummaryView[] {
    // During month loading, use stored articles to prevent scroll jump
    if (this.isLoadingMonth() && this.articlesDuringMonthLoad.length > 0) {
      return this.articlesDuringMonthLoad;
    }

    const articles = this.articles();

    if (!articles || articles.length === 0) {
      return [];
    }

    // Sort articles by date (newest first)
    return [...articles].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  /**
   * Get articles for display - sliding window of 200 articles
   */
  getSortedArticles(): ArticleSummaryView[] {
    const allArticles = this.getAllSortedArticles();
    const startIndex = this.visibleStartIndex();
    const endIndex = startIndex + 200;

    // Return a sliding window of 200 articles
    return allArticles.slice(startIndex, endIndex);
  }

  viewArticle(articleId: number) {
    // Save current article, scroll position and selected year before navigation
    const selectedYear = this.selectedYear();
    const currentStartIndex = this.visibleStartIndex();
    const scrollPosition = this.getCurrentScrollPosition();

    // Save state to localStorage for persistence across navigation
    localStorage.setItem(
      'articleListReturnState',
      JSON.stringify({
        returnYear: selectedYear,
        returnArticleId: articleId,
        returnStartIndex: currentStartIndex,
        returnScroll: scrollPosition,
      }),
    );

    // Navigate to article detail page
    void this.router.navigate(['/articles', articleId]);
  }

  /**
   * Check if the scrollable container is available
   */
  private isScrollableContainerAvailable(): boolean {
    return !!this.scrollableContainer?.nativeElement;
  }

  /**
   * Set up detection for page refresh vs navigation
   */
  private setupPageRefreshDetection(): void {
    // Check if we have return state from localStorage
    const savedState = localStorage.getItem('articleListReturnState');

    // If we have saved state, it means we're coming back from navigation
    if (savedState) {
      this.isPageRefresh = false;
    } else {
      // No saved state, likely a page refresh or direct navigation
      this.isPageRefresh = true;
    }
  }

  /**
   * Restore the saved article position from localStorage
   */
  private restoreArticlePosition(): void {
    const parsedState = this.parseSavedState();

    if (parsedState) {
      // Restore the start index
      this.visibleStartIndex.set(parsedState.returnStartIndex);

      // Wait for DOM to update, then restore scroll position
      setTimeout(() => {
        if (this.isScrollableContainerAvailable()) {
          // Use the saved scroll position for exact restoration
          this.setScrollPosition(parsedState.returnScroll);
        }
      }, 10);
    }
  }

  /**
   * Restore the saved selected year from localStorage
   */
  private restoreSelectedYear(articlesYearlyBreakdown: ArticleYearlyBreakdownView[]): void {
    if (articlesYearlyBreakdown.length === 0) {
      return;
    }

    let loadYear = articlesYearlyBreakdown[0].year;

    // Try to get state from localStorage
    const parsedState = this.parseSavedState();
    if (parsedState) {
      // Only restore if the year exists in the available years
      const yearExists = articlesYearlyBreakdown.some((y) => Number(y.year) === parsedState.returnYear);

      if (yearExists) {
        loadYear = parsedState.returnYear;
      }
    }

    this.store.dispatch(ArticlesActions.selectYear({ year: loadYear }));

    // If we have return article, restore it after a delay
    if (parsedState) {
      this.restoreArticlePosition();

      // Wait for scroll to be restored and then clean up localStorage after restoring state
      setTimeout(() => {
        localStorage.removeItem('articleListReturnState');
      }, 1500);
    }
  }

  private setupScrollbarVisibility() {
    // Wait a bit to ensure the element is available
    setTimeout(() => {
      if (this.isScrollableContainerAvailable()) {
        const element = this.scrollableContainer.nativeElement;
        let scrollTimeout: NodeJS.Timeout;
        let isScrolling = false;

        element.addEventListener('scroll', () => {
          if (!isScrolling) {
            isScrolling = true;
            this.scrollbarVisible = true;
          }

          // Hide the scrollbar after 2 seconds of inactivity
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            isScrolling = false;
            this.scrollbarVisible = false;
          }, 2000);
        });

        // Show the scrollbar on hover
        element.addEventListener('mouseenter', () => {
          this.scrollbarVisible = true;
        });

        // Hide the scrollbar when leaving the area (with delay)
        element.addEventListener('mouseleave', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            if (!isScrolling) {
              this.scrollbarVisible = false;
            }
          }, 1000); // 1 second delay after leaving the area
        });
      }
    }, 100);
  }

  private setupScrollListener() {
    if (this.scrollableContainer?.nativeElement) {
      const container = this.scrollableContainer.nativeElement;

      // Add scroll listener to detect visible articles and update selected month
      container.addEventListener('scroll', () => {
        this.updateSelectedMonthBasedOnVisibleArticles();
        this.checkForLoadMore();
      });

      // Initial call to set the correct month on load
      setTimeout(() => {
        this.updateSelectedMonthBasedOnVisibleArticles();
      }, 300);
    }
  }

  private updateSelectedMonthBasedOnVisibleArticles() {
    if (!this.scrollableContainer?.nativeElement) {
      return;
    }

    const container = this.scrollableContainer.nativeElement;
    const containerRect = container.getBoundingClientRect();
    const articles = this.getAllSortedArticles();

    if (articles.length === 0) {
      return;
    }

    // Find articles that are visible in the viewport
    const visibleArticles = articles.filter((article) => {
      const articleElement = document.querySelector(`[data-article-id="${article.nodeId}"]`);
      if (!articleElement) return false;

      const articleRect = articleElement.getBoundingClientRect();

      // Check if article is at least partially visible in the container
      return articleRect.bottom > containerRect.top && articleRect.top < containerRect.bottom;
    });

    if (visibleArticles.length === 0) {
      return;
    }

    // Count articles by month
    const monthCounts = new Map<number, number>();
    visibleArticles.forEach((article) => {
      const articleMonth = new Date(article.createdAt).getMonth() + 1; // getMonth() returns 0-11
      monthCounts.set(articleMonth, (monthCounts.get(articleMonth) ?? 0) + 1);
    });

    // Find the month with the most visible articles
    let dominantMonth = null;
    let maxCount = 0;

    for (const [month, count] of monthCounts.entries()) {
      if (count > maxCount) {
        maxCount = count;
        dominantMonth = month;
      }
    }

    // Update selectedMonth based on visible articles
    // Only prevent auto-update if user recently clicked a month (within 3 seconds)
    if (dominantMonth !== null && dominantMonth !== this.selectedMonth()) {
      // Check if user recently selected a month manually
      const timeSinceLastUserSelection = Date.now() - (this.lastUserSelectionTime || 0);
      const shouldAllowAutoUpdate = timeSinceLastUserSelection > 3000 || this.lastUserSelectionTime === 0; // 3 seconds or never selected

      if (shouldAllowAutoUpdate) {
        this.selectedMonth.set(dominantMonth);
      }
    }
  }

  /**
   * Check if we need to load more articles based on scroll position
   */
  private checkForLoadMore(): void {
    if (!this.scrollableContainer?.nativeElement) {
      return;
    }

    // Don't trigger auto-loading during month navigation
    if (this.isNavigatingToMonth) {
      return;
    }

    const allArticles = this.getAllSortedArticles();
    const totalArticles = allArticles.length;
    const currentStartIndex = this.visibleStartIndex();
    const currentEndIndex = currentStartIndex + 200;

    // Check if user is viewing articles near the end of currently visible articles

    // Check if we can load more articles in each direction
    const canLoadMoreDown = currentEndIndex < totalArticles;
    const canLoadMoreUp = currentStartIndex > 0;

    // Check if user is viewing articles near the end/beginning of visible articles
    const isNearEndOfVisible = this.isNearEndOfVisibleArticles();
    const isNearStartOfVisible = this.isNearStartOfVisibleArticles();

    // Load more articles downward if viewing near the end of visible articles
    if (isNearEndOfVisible && canLoadMoreDown && !this.isLoadingMore()) {
      this.loadMoreVisibleArticlesDown();
    }

    // Load more articles upward if viewing near the start of visible articles
    if (isNearStartOfVisible && canLoadMoreUp && !this.isLoadingMore()) {
      this.loadMoreVisibleArticlesUp();
    }
  }

  /**
   * Check if user is viewing articles near the end of currently visible articles
   */
  private isNearEndOfVisibleArticles(): boolean {
    if (!this.scrollableContainer?.nativeElement) {
      return false;
    }

    const container = this.scrollableContainer.nativeElement;
    const containerRect = container.getBoundingClientRect();
    const containerBottom = containerRect.bottom;

    // Get all visible article elements
    const articleElements = Array.from(document.querySelectorAll(this.ARTICLE_SELECTOR));

    // Find the last visible article index
    let lastVisibleArticleIndex = -1;

    for (let i = 0; i < articleElements.length; i++) {
      const articleElement = articleElements[i];
      const articleRect = articleElement.getBoundingClientRect();

      if (articleRect.bottom > containerRect.top && articleRect.top < containerBottom) {
        lastVisibleArticleIndex = i;
      }
    }

    // Check if we're viewing articles near the end (within the last 50 visible articles)
    const threshold = Math.min(50, articleElements.length);
    return lastVisibleArticleIndex >= articleElements.length - threshold;
  }

  /**
   * Check if user is viewing articles near the start of currently visible articles
   */
  private isNearStartOfVisibleArticles(): boolean {
    if (!this.scrollableContainer?.nativeElement) {
      return false;
    }

    const container = this.scrollableContainer.nativeElement;
    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top;

    // Get all visible article elements
    const articleElements = Array.from(document.querySelectorAll(this.ARTICLE_SELECTOR));

    // Find the first visible article
    let firstVisibleArticleIndex = -1;

    for (let i = 0; i < articleElements.length; i++) {
      const articleElement = articleElements[i];
      const articleRect = articleElement.getBoundingClientRect();

      if (articleRect.bottom > containerTop && articleRect.top < containerRect.bottom) {
        firstVisibleArticleIndex = i;
        break;
      }
    }

    // Check if we're viewing articles near the start (within the first 50 visible articles)
    const threshold = Math.min(50, articleElements.length);
    return firstVisibleArticleIndex >= 0 && firstVisibleArticleIndex < threshold;
  }

  /**
   * Load more articles downward (original behavior)
   */
  private loadMoreVisibleArticlesDown(): void {
    if (this.isLoadingMore()) {
      return;
    }

    const allArticles = this.getAllSortedArticles();
    const currentStartIndex = this.visibleStartIndex();
    const currentEndIndex = currentStartIndex + 200;

    // Check if we have more articles to show
    if (currentEndIndex >= allArticles.length) {
      return;
    }

    // Find the currently visible article to maintain its position
    const container = this.scrollableContainer?.nativeElement;
    let referenceArticleId: number | null = null;
    let referenceArticleOffset = 0;

    if (container) {
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top;

      // Find the first article that is visible in the viewport
      const articleElements = Array.from(document.querySelectorAll(this.ARTICLE_SELECTOR));
      for (const articleElement of articleElements) {
        const articleRect = articleElement.getBoundingClientRect();
        if (articleRect.bottom > containerTop && articleRect.top < containerRect.bottom) {
          referenceArticleId = parseInt(articleElement.getAttribute('data-article-id') ?? '0');
          referenceArticleOffset = articleRect.top - containerTop;
          break;
        }
      }
    }

    // Set loading state
    this.isLoadingMore.set(true);

    // Update start index to load more articles downward
    const newStartIndex = Math.min(currentStartIndex + this.articlesPerLoad, allArticles.length - 200);

    this.visibleStartIndex.set(newStartIndex);
    this.isLoadingMore.set(false);

    // Restore the position of the reference article immediately
    if (referenceArticleId && container) {
      // Use requestAnimationFrame to ensure DOM is updated before adjusting scroll
      requestAnimationFrame(() => {
        const referenceElement = document.querySelector(`[data-article-id="${referenceArticleId}"]`);
        if (referenceElement) {
          const containerRect = container.getBoundingClientRect();
          const articleRect = referenceElement.getBoundingClientRect();
          const targetScrollTop = container.scrollTop + (articleRect.top - containerRect.top) - referenceArticleOffset;
          container.scrollTop = targetScrollTop;
        }
      });
    }
  }

  /**
   * Load more articles upward
   */
  private loadMoreVisibleArticlesUp(): void {
    if (this.isLoadingMore()) {
      return;
    }

    const currentStartIndex = this.visibleStartIndex();

    // Check if we have more articles to show upward
    if (currentStartIndex <= 0) {
      return;
    }

    // Find the currently visible article to maintain its position
    const container = this.scrollableContainer?.nativeElement;
    let referenceArticleId: number | null = null;
    let referenceArticleOffset = 0;

    if (container) {
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top;

      // Find the first article that is visible in the viewport
      const articleElements = Array.from(document.querySelectorAll(this.ARTICLE_SELECTOR));
      for (const articleElement of articleElements) {
        const articleRect = articleElement.getBoundingClientRect();
        if (articleRect.bottom > containerTop && articleRect.top < containerRect.bottom) {
          referenceArticleId = parseInt(articleElement.getAttribute('data-article-id') ?? '0');
          referenceArticleOffset = articleRect.top - containerTop;
          break;
        }
      }
    }

    // Set loading state
    this.isLoadingMore.set(true);

    // Update start index to load more articles upward
    const newStartIndex = Math.max(currentStartIndex - this.articlesPerLoad, 0);

    this.visibleStartIndex.set(newStartIndex);
    this.isLoadingMore.set(false);

    // Restore the position of the reference article immediately
    if (referenceArticleId && container) {
      // Use requestAnimationFrame to ensure DOM is updated before adjusting scroll
      requestAnimationFrame(() => {
        const referenceElement = document.querySelector(`[data-article-id="${referenceArticleId}"]`);
        if (referenceElement) {
          const containerRect = container.getBoundingClientRect();
          const articleRect = referenceElement.getBoundingClientRect();
          const targetScrollTop = container.scrollTop + (articleRect.top - containerRect.top) - referenceArticleOffset;
          container.scrollTop = targetScrollTop;
        }
      });
    }
  }
}
